import React, { useMemo, useState } from 'react';

import { groupBy, sortBy } from 'lodash';
import { Link } from '~/lib/react-router-dom';

import AssessmentIcon from '@mui/icons-material/Assessment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { formatDateSlashWithTime } from '~/services/TimeService';

import type { ContractingQueueCase } from '~/types/services/contracting';

import CaseClampedText from './CaseClampedText';
import CaseExpandedDetails from './CaseExpandedDetails';
import { useExpandedCaseRows } from './useExpandedCaseRows';

// Widths sum to 100% so the table fits the page; long text clamps instead of scrolling.
// Case Last Modified Date, Booking Check-In Date and Case Reason live only in the expanded row.
const EXPAND_COLUMN_WIDTH = '3%';
const COLUMNS = [
  { label: 'Subject', width: '36%' },
  { label: 'Date/Time Opened', width: '12%' },
  { label: 'Open', width: '5%', isCentered: true },
  { label: 'Closed', width: '6%', isCentered: true },
  { label: 'Status', width: '16%' },
  { label: 'Priority', width: '8%' },
  { label: 'Opportunity', width: '14%' },
];

const EMPTY_VALUE = '-';
const UNKNOWN_OWNER = 'Unknown owner';
const COLUMN_COUNT = COLUMNS.length + 1;
const headerCellSx = { fontWeight: 'bold', verticalAlign: 'bottom' } as const;
const controlCellSx = { verticalAlign: 'middle' } as const;

interface Props {
  cases: Array<ContractingQueueCase>;
  buildCaseHref: (caseNumber: string) => string;
}

export default function CasesByOwnerPage({ cases, buildCaseHref }: Props) {
  const [search, setSearch] = useState('');
  const { isExpanded, toggle, handleRowClick } = useExpandedCaseRows();

  const groups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matching = cases.filter(
      (item) =>
        !query ||
        [item.owner.name, item.subject, item.status, item.reason, item.opportunity?.name]
          .join(' ')
          .toLowerCase()
          .includes(query),
    );
    // By id, so two owners who share a name stay apart.
    const byOwner = groupBy(matching, (item) => item.owner.external_refs.salesforce);
    return sortBy(
      Object.values(byOwner).map((ownerCases) => ({
        ownerId: ownerCases[0].owner.external_refs.salesforce,
        ownerName: ownerCases[0].owner.name ?? UNKNOWN_OWNER,
        cases: ownerCases,
      })),
      'ownerName',
    );
  }, [cases, search]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: 'success.main',
              color: 'common.white',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <AssessmentIcon />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Report: Cases
            </Typography>
            <Typography variant="h6" component="h2">
              Campaign Operations Cases
            </Typography>
          </Box>
        </Stack>
        <TextField
          size="small"
          type="search"
          placeholder="Search..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ width: 240 }}
          slotProps={{
            htmlInput: { 'aria-label': 'Search cases' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Stack>
      <Table size="small" aria-label="Campaign operations cases by owner" sx={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          <col style={{ width: EXPAND_COLUMN_WIDTH }} />
          {COLUMNS.map((column) => (
            <col key={column.label} style={{ width: column.width }} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell />
            {COLUMNS.map((column) => (
              <TableCell key={column.label} align={column.isCentered ? 'center' : 'left'} sx={headerCellSx}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.length === 0 && (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT}>
                <Typography color="text.secondary">
                  {search.trim() ? 'No cases match that search.' : 'No cases to show.'}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {groups.map(({ ownerId, ownerName, cases: ownerCases }) => (
            <React.Fragment key={ownerId}>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell colSpan={COLUMN_COUNT} sx={{ fontWeight: 'bold' }}>
                  {ownerName} ({ownerCases.length})
                </TableCell>
              </TableRow>
              {ownerCases.map((item) => {
                const isOpen = isExpanded(item.case_number);

                return (
                  <React.Fragment key={item.case_number}>
                    <TableRow
                      hover
                      onClick={(event) => handleRowClick(event, item.case_number)}
                      sx={{
                        verticalAlign: 'top',
                        cursor: 'pointer',
                        '& > td': isOpen ? { borderBottom: 0 } : undefined,
                      }}
                    >
                      <TableCell padding="none" align="center" sx={controlCellSx}>
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          aria-label={isOpen ? `Collapse case ${item.case_number}` : `Expand case ${item.case_number}`}
                          aria-expanded={isOpen}
                          onClick={() => toggle(item.case_number)}
                        >
                          {isOpen ? (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {/* The subject is this tab's only link to the case, so a case without one shows its number. */}
                        <CaseClampedText text={item.subject ?? item.case_number} maxLines={3}>
                          <MuiLink component={Link} to={buildCaseHref(item.case_number)} underline="hover">
                            {item.subject ?? item.case_number}
                          </MuiLink>
                        </CaseClampedText>
                      </TableCell>
                      <TableCell>
                        <CaseClampedText text={formatDateSlashWithTime(item.created_at)} />
                      </TableCell>
                      <TableCell align="center" sx={controlCellSx}>
                        <Checkbox
                          size="small"
                          checked={!item.is_closed}
                          disabled
                          slotProps={{ input: { 'aria-label': 'Open' } }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={controlCellSx}>
                        <Checkbox
                          size="small"
                          checked={item.is_closed}
                          disabled
                          slotProps={{ input: { 'aria-label': 'Closed' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <CaseClampedText text={item.status} />
                      </TableCell>
                      <TableCell>
                        <CaseClampedText text={item.priority} maxLines={1} />
                      </TableCell>
                      <TableCell>
                        <CaseClampedText text={item.opportunity?.name || EMPTY_VALUE} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={COLUMN_COUNT} sx={{ p: 0, borderBottom: isOpen ? undefined : 0 }}>
                        <Collapse in={isOpen} unmountOnExit>
                          <CaseExpandedDetails caseItem={item} caseHref={buildCaseHref(item.case_number)} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
